/**
 * Sort de la place d'une source si on la supprimait maintenant (DEV-621,
 * boîtes de suppression de l'écran M4).
 *
 * INTENTION. Avant de confirmer, le client lit ce que la suppression ferait de
 * sa place dans le forfait : « Cette source restera facturée jusqu'au JJ/MM
 * (ajoutée pendant la période) » ou « Sa place sera libre dès la suppression. ». Le
 * serveur juge (`GET /v5/source/:id/billing/si-supprimee`, bloc
 * `si_supprimee`) ; l'écran ne recalcule jamais la règle. Bloc absent, nul,
 * incohérent ou appel en échec : aucune phrase, jamais de promesse inventée.
 *
 * Règles de rédaction : « facturée », aucun nom de marque, aucun détail
 * technique, dates JJ/MM à l'heure de Paris.
 */
import { formatDate } from "@core/helpers"

/** Jugement servi pour une source supposée supprimée maintenant */
export type SiSupprimee = {
    /** Vrai : la source resterait facturée jusqu'à la fin de la période */
    comptee: boolean
    /** Fin de la période si elle resterait facturée, sinon null */
    comptee_jusqu_au: string | null
    /** `supprimee_ajoutee_dans_la_periode`, `supprimee_apres_mise_a_jour` ou `supprimee` */
    motif: string
}

/** Réponse de `GET /v5/source/:id/billing/si-supprimee` */
export type ReponseSiSupprimee = {
    si_supprimee: SiSupprimee | null
    periode_fin: string | null
}

const PRECISION_PAR_MOTIF: Record<string, string> = {
    supprimee_ajoutee_dans_la_periode: "ajoutée pendant la période",
    supprimee_apres_mise_a_jour: "mise à jour pendant la période",
}

/** Phrase quand la place serait libre tout de suite */
export const PHRASE_PLACE_LIBRE = "Sa place sera libre dès la suppression."

/**
 * Phrase de la boîte, lue du seul jugement servi :
 * - `comptee` vrai et motif d'ajout ou de mise à jour : « Cette source
 *   restera facturée jusqu'au JJ/MM (ajoutée pendant la période). » ou « (mise à jour
 *   pendant la période). », date `comptee_jusqu_au`, sinon `periodeFin` servie ;
 * - `comptee` faux et motif `supprimee` : « Sa place sera libre dès la
 *   suppression. » ;
 * - tout autre cas (bloc nul, incohérent, sans date) : `null`.
 * @param si - bloc `si_supprimee` servi
 * @param periodeFin - fin de la période servie avec le bloc, si connue
 */
export function phraseSiSupprimee(
    si: unknown,
    periodeFin?: string | null
): string | null {
    if (!si || typeof si !== "object") return null
    const { comptee, comptee_jusqu_au, motif } = si as Partial<SiSupprimee>
    if (comptee === false) {
        return motif === "supprimee" ? PHRASE_PLACE_LIBRE : null
    }
    if (comptee !== true || typeof motif !== "string") return null
    const precision = PRECISION_PAR_MOTIF[motif]
    if (!precision) return null
    const fin =
        formatDate(comptee_jusqu_au, "DD/MM", "") ||
        formatDate(periodeFin, "DD/MM", "")
    if (!fin) return null
    return `Cette source restera facturée jusqu'au ${fin} (${precision}).`
}

/**
 * Phrase tirée de la réponse de l'appel ciblé, `null` si la réponse est
 * absente ou illisible.
 */
export function phraseReponseSiSupprimee(reponse: unknown): string | null {
    if (!reponse || typeof reponse !== "object") return null
    const r = reponse as Partial<ReponseSiSupprimee>
    return phraseSiSupprimee(
        r.si_supprimee,
        typeof r.periode_fin === "string" ? r.periode_fin : null
    )
}
